import { useCallback, useMemo } from 'react';
import {
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { TpSlTriggerPercentBasis } from 'src/components/TradeForm/TpSlOrderForm/types';
import { getMarginUsedForPosition } from '@velocity-exchange/common';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import { useCollateralValueRequiredForTrade } from 'src/components/TradeForm/hooks/useCollateralValueRequiredForTrade';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';

interface Params {
	triggerPercentBasis: TpSlTriggerPercentBasis;
	positionIsLong: boolean;
	entryPrice: BigNum;
	entrySize: BigNum;
	marketIndex?: number;
}

export default function useTpSlOrderFormCalcs({
	triggerPercentBasis,
	positionIsLong,
	entryPrice,
	entrySize,
	marketIndex,
}: Params) {
	const user = useVelocityAccountStore(
		(s) => s.getCurrentUserAccount()?.client
	);
	const userPositions = useCurrentAccountPositions();

	const collateralValueRequiredForTrade = useCollateralValueRequiredForTrade({
		baseSizeString: entrySize.toNum().toString(),
		marketIndex,
		estEntryPrice: entryPrice.val,
	});

	const hasPosition = useMemo(() => {
		if (!user || marketIndex === undefined) {
			return false;
		}

		const position = userPositions?.find((p) => p.marketIndex === marketIndex);
		return position && !position?.baseSize.isZero();
	}, [marketIndex, userPositions]);

	const marginUsedForPosition = useMemo(() => {
		if (!user || marketIndex === undefined) {
			return BigNum.zero(QUOTE_PRECISION_EXP);
		}

		return BigNum.from(
			getMarginUsedForPosition(user, marketIndex, false),
			QUOTE_PRECISION_EXP
		);
	}, [marketIndex, user]);

	const collateralForCalculation = hasPosition
		? marginUsedForPosition
		: collateralValueRequiredForTrade;

	const calcPercent = useCallback(
		(newPrice: BigNum): number => {
			// Calculate based on price move.
			if (triggerPercentBasis === 'price') {
				const diff = entryPrice.sub(newPrice).abs();

				if (diff.eqZero() || entryPrice.eqZero()) {
					return 0;
				}

				const result = diff.toPercentage(
					entryPrice,
					PRICE_PRECISION_EXP.toNumber()
				);

				return Number(result);
			}

			// Calculate the ROI %.

			const collateralNum = collateralForCalculation.toNum();

			if (!collateralNum) {
				return 0;
			}

			// Have to convert to numbers here otherwise I'm seeing small numbers
			// (specifically happened with 10^{-3}) get rounded down to 0 by BigNum.
			const entrySizeNum = entrySize.toNum();
			const entryPriceNum = entryPrice.toNum();
			const newPriceNum = newPrice.toNum();

			// For longs: PnL = (exitPrice - entryPrice) * size
			// For shorts: PnL = (entryPrice - exitPrice) * size
			const pnl = positionIsLong
				? (newPriceNum - entryPriceNum) * entrySizeNum
				: (entryPriceNum - newPriceNum) * entrySizeNum;

			// ROI % = (PnL / required collateral) * 100
			return Math.abs((pnl / collateralNum) * 100);
		},
		[
			collateralForCalculation,
			entryPrice,
			entrySize,
			triggerPercentBasis,
			positionIsLong,
		]
	);

	const calcPrice = useCallback(
		(percentMove: number, isTakeProfit: boolean): BigNum => {
			// Calculate based on price move.
			if (triggerPercentBasis === 'price') {
				// Use more precise calculation for small percentages
				const entryPriceNum = entryPrice.toNum();
				const percentDecimal = percentMove / 100;
				const priceChange = entryPriceNum * percentDecimal;
				const diff = BigNum.fromPrint(
					priceChange.toString(),
					entryPrice.precision
				);

				if (positionIsLong) {
					if (isTakeProfit) {
						return entryPrice.add(diff);
					}
					return entryPrice.sub(diff);
				}

				if (isTakeProfit) {
					return entryPrice.sub(diff);
				}
				return entryPrice.add(diff);
			}

			// Derive final price from ROI %.
			const collateralNum = collateralForCalculation.toNum();
			const targetPnL =
				collateralNum * ((percentMove * (isTakeProfit ? 1 : -1)) / 100);

			const entrySizeNum = entrySize.toNum();
			const entryPriceNum = entryPrice.toNum();
			const priceChange = targetPnL / entrySizeNum;

			const finalPriceNum = positionIsLong
				? entryPriceNum + priceChange
				: entryPriceNum - priceChange;

			return BigNum.fromPrint(finalPriceNum.toString(), entryPrice.precision);
		},
		[
			collateralForCalculation,
			entryPrice,
			entrySize,
			positionIsLong,
			triggerPercentBasis,
		]
	);

	return {
		calcPercent,
		calcPrice,
	};
}
